<template>
    <div>
        <div class="content-header row">
            <div class="content-header-left col-md-6 col-12 mb-2">
                <h3 class="content-header-title mb-0">{{ isEditing ? 'Edit' : 'Add' }} Blog</h3>
                <div class="row breadcrumbs-top">
                    <div class="breadcrumb-wrapper col-12">
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><a href="/">Home</a></li>
                            <li class="breadcrumb-item">
                                <Link :href="route('admin.blogs.index')">Blogs</Link>
                            </li>
                            <li class="breadcrumb-item active">{{ isEditing ? 'Edit' : 'Add' }} Blog</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>

        <div class="content-body">
            <section class="card">
                <div class="card-body">
                    <form @submit.prevent="saveBlog">
                        <div class="row">
                            <div class="col-12 col-sm-6">
                                <div class="form-group">
                                    <label>Title</label>
                                    <input type="text" v-model="blog.title" class="form-control" placeholder="Blog title" required>
                                    <div v-if="blog.errors.title" class="text-danger text-small">{{ blog.errors.title }}</div>
                                </div>
                            </div>
                            <div class="col-12 col-sm-6">
                                <div class="form-group">
                                    <label>Category</label>
                                    <select class="form-control" v-model="blog.blog_category_id" required>
                                        <option value="">Select category</option>
                                        <option v-for="category in categories" :key="category.id" :value="category.id">
                                            {{ category.name }}
                                        </option>
                                    </select>
                                    <div v-if="blog.errors.blog_category_id" class="text-danger text-small">{{ blog.errors.blog_category_id }}</div>
                                </div>
                            </div>
                            <div class="col-12 col-sm-6">
                                <div class="form-group mb-1">
                                    <label>Author</label>
                                    <input type="text" v-model="blog.author_name" class="form-control" placeholder="Author name" required>
                                    <div v-if="blog.errors.author_name" class="text-danger text-small">{{ blog.errors.author_name }}</div>
                                </div>
                            </div>
                            <div class="col-12 col-sm-6">
                                <div class="form-group mb-1">
                                    <label>Published date</label>
                                    <input type="date" v-model="blog.published_at" class="form-control">
                                </div>
                            </div>
                            <div class="col-12">
                                <div class="form-group">
                                    <div class="form-check">
                                        <input class="form-check-input" type="checkbox" v-model="blog.status" :true-value="true" :false-value="false">
                                        <label class="form-check-label">Active</label>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12">
                                <div class="form-group">
                                    <label>Excerpt</label>
                                    <textarea v-model="blog.excerpt" class="form-control" rows="3" placeholder="Short summary for the listing card" required></textarea>
                                    <div v-if="blog.errors.excerpt" class="text-danger text-small">{{ blog.errors.excerpt }}</div>
                                </div>
                            </div>
                            <div class="col-12 col-sm-6 mb-3">
                                <label>Cover image</label><br>
                                <img v-if="blog.cover_image" :src="blog.cover_image" alt="" style="max-width: 100%; height: auto; border-radius: 3px; margin-bottom: 10px;">
                                <button type="button" class="form-control" @click="openGallery('cover_image')" data-toggle="modal" data-target="#gallery-modal">
                                    {{ blog.cover_image ? 'Change' : 'Choose' }} Cover Image
                                </button>
                                <div v-if="blog.errors.cover_image" class="text-danger text-small">{{ blog.errors.cover_image }}</div>
                            </div>
                            <div class="col-12 col-sm-6 mb-3">
                                <label>Body image</label><br>
                                <img v-if="blog.body_image" :src="blog.body_image" alt="" style="max-width: 100%; height: auto; border-radius: 3px; margin-bottom: 10px;">
                                <button type="button" class="form-control" @click="openGallery('body_image')" data-toggle="modal" data-target="#gallery-modal">
                                    {{ blog.body_image ? 'Change' : 'Choose' }} Body Image
                                </button>
                            </div>
                            <div class="col-12">
                                <div class="form-group">
                                    <label>Body</label>
                                    <vue-editor v-model="blog.body"></vue-editor>
                                    <div v-if="blog.errors.body" class="text-danger text-small">{{ blog.errors.body }}</div>
                                </div>
                            </div>
                            <div class="col-12 mb-3">
                                <label>Images after first body</label>
                                <p class="text-muted text-small mb-1">Optional. These appear after the first body, before any additional bodies.</p>
                                <div class="d-flex flex-wrap">
                                    <div v-for="(image, imageIndex) in blog.body_images" :key="'body-image-'+imageIndex" class="mr-2 mb-2">
                                        <img :src="image" alt="" style="max-width: 140px; height: auto; border-radius: 3px; display: block; margin-bottom: 6px;">
                                        <button type="button" class="btn btn-sm btn-outline-danger" @click="removeBodyImage(imageIndex)">Remove</button>
                                    </div>
                                </div>
                                <button type="button" class="btn btn-outline-secondary btn-sm" @click="openGallery('body_images')" data-toggle="modal" data-target="#gallery-modal">
                                    Add image after first body
                                </button>
                                <div v-if="blog.errors.body_images" class="text-danger text-small">{{ blog.errors.body_images }}</div>
                            </div>
                            <div class="col-12" v-for="(section, index) in blog.sections" :key="'section-'+index">
                                <div class="card border mb-2">
                                    <div class="card-header d-flex justify-content-between align-items-center">
                                        <strong>Additional body {{ index + 2 }}</strong>
                                        <button type="button" class="btn btn-sm btn-outline-danger" @click="removeSection(index)">Remove this body</button>
                                    </div>
                                    <div class="card-body">
                                        <div class="form-group">
                                            <label>Body</label>
                                            <vue-editor v-model="section.body"></vue-editor>
                                            <div v-if="blog.errors['sections.'+index+'.body']" class="text-danger text-small">{{ blog.errors['sections.'+index+'.body'] }}</div>
                                        </div>
                                        <label>Images</label>
                                        <p class="text-muted text-small mb-1">Add one or more images that should appear after this body.</p>
                                        <div class="d-flex flex-wrap">
                                            <div v-for="(image, imageIndex) in section.images" :key="'section-'+index+'-image-'+imageIndex" class="mr-2 mb-2">
                                                <img :src="image" alt="" style="max-width: 140px; height: auto; border-radius: 3px; display: block; margin-bottom: 6px;">
                                                <button type="button" class="btn btn-sm btn-outline-danger" @click="removeSectionImage(index, imageIndex)">Remove</button>
                                            </div>
                                        </div>
                                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="openGallery({ kind: 'section', index: index })" data-toggle="modal" data-target="#gallery-modal">
                                            Add image
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 mb-3">
                                <button type="button" class="btn btn-outline-primary" @click="addSection">Add more body</button>
                            </div>
                            <div class="col-12 d-flex flex-sm-row flex-column justify-content-end mt-1">
                                <button type="submit" class="btn btn-primary glow mb-1 mb-sm-0 mr-0 mr-sm-1" :disabled="saving">
                                    {{ isEditing ? 'Update' : 'Save' }}
                                    <i class="fa fa-spinner fa-spin" v-if="saving"/>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </section>
        </div>
        <gallery-modal @chooseImage="chosenImage" gallery-header="Choose blog image" :multiple-images="isMultiImagePicker"></gallery-modal>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout';
import {Link} from "@inertiajs/vue2";
import GalleryModal from "@/Shared/GalleryModal";
import { VueEditor } from "vue2-editor";

export default {
    metaInfo: {title: 'Blog'},
    props: ['isEditing', 'blogData', 'categories'],
    components: { Link, GalleryModal, VueEditor },
    layout: Layout,
    data() {
        return {
            imageField: 'cover_image',
            blog: this.$inertia.form({
                title: '',
                blog_category_id: '',
                author_name: '',
                published_at: '',
                excerpt: '',
                body: '',
                cover_image: '',
                body_image: '',
                body_images: [],
                sections: [],
                status: true,
            }),
            saving: false
        }
    },
    computed: {
        isMultiImagePicker() {
            return this.imageField === 'body_images'
                || (this.imageField && this.imageField.kind === 'section');
        }
    },
    methods: {
        openGallery(field) {
            this.imageField = field;
        },
        imageUrls(image) {
            if (Array.isArray(image)) {
                return image.filter(item => item && item.image).map(item => item.image);
            }

            return image && image.image ? [image.image] : [];
        },
        chosenImage(image) {
            const urls = this.imageUrls(image);
            if (!urls.length) {
                return;
            }

            if (this.imageField === 'body_images') {
                this.blog.body_images = this.blog.body_images.concat(urls);
                return;
            }

            if (this.imageField && this.imageField.kind === 'section') {
                const section = this.blog.sections[this.imageField.index];
                section.images = section.images.concat(urls);
                return;
            }

            this.blog[this.imageField] = urls[0];
        },
        addSection() {
            this.blog.sections.push({ body: '', images: [] });
        },
        removeSection(index) {
            this.blog.sections.splice(index, 1);
        },
        removeBodyImage(index) {
            this.blog.body_images.splice(index, 1);
        },
        removeSectionImage(sectionIndex, imageIndex) {
            this.blog.sections[sectionIndex].images.splice(imageIndex, 1);
        },
        async saveBlog() {
            this.saving = true;
            const options = {
                preserveScroll: true,
                onSuccess: () => this.saving = false,
                onError: () => this.saving = false
            };

            if (this.isEditing) {
                this.blog.put(route('admin.blogs.update', this.blogData.id), options);
            } else {
                this.blog.post(route('admin.blogs.store'), options);
            }
        }
    },
    mounted() {
        if (this.isEditing && this.blogData) {
            this.blog.title = this.blogData.title;
            this.blog.blog_category_id = this.blogData.blog_category_id;
            this.blog.author_name = this.blogData.author_name;
            this.blog.published_at = this.blogData.published_at ? String(this.blogData.published_at).substring(0, 10) : '';
            this.blog.excerpt = this.blogData.excerpt;
            this.blog.body = this.blogData.body;
            this.blog.cover_image = this.blogData.cover_image;
            this.blog.body_image = this.blogData.body_image || '';
            this.blog.body_images = this.blogData.body_images || [];
            this.blog.sections = (this.blogData.sections || []).map(section => ({
                body: section.body || '',
                images: section.images || [],
            }));
            this.blog.status = this.blogData.status;
        }
    },
}
</script>
